import React, { ReactNode } from 'react';
import { FunctionComponentReturnType } from 'src/types/sharedReact';
import { Table } from '@c1/gravity-react';
import { Column } from 'src/types/gravity';
import { MonitorTableDataSource, TableFiltersType } from 'src/types/shared';
import { createFilterableColumn, createPlainColumn, filterItem } from 'src/utils/tableUtils';
import ConfirmDeleteModal from 'src/components/modals/ConfirmDeleteModal/ConfirmDeleteModal';
import MonitorTableActionTray from 'src/components/tables/MonitorTableActionTray/MonitorTableActionTray';
import 'src/components/tables/MonitorTable/MonitorTable.css';
import Button from '@c1/gravity-react/lib/Button';

export interface ColumnTitleAndDataIndex<ItemType extends Record<string, any>> {
    title: string;
    dataIndex: Exclude<keyof ItemType, number | symbol>;
    filterable?: boolean;
}

type ItemToString<ItemType> = (item: ItemType) => string;

export interface MonitorTableProps<ItemType extends Record<string, any>> {
    items: ItemType[];
    columns: ColumnTitleAndDataIndex<ItemType>[];
    getKey: ItemToString<ItemType>;
    modalTitle: string;
    getModalDescription: (item: ItemType) => ReactNode;
    getApiRouteToDelete: ItemToString<ItemType>;
    refreshData: () => void;
    currentPage?: number | null;
    totalPages?: number | null;
    tableUpdate?: (action: string, data: string) => void | null;
}

function MonitorTable<ItemType extends Record<string, any>>(
    {
        items,
        columns,
        getKey,
        getApiRouteToDelete,
        getModalDescription,
        modalTitle,
        refreshData,
        currentPage,
        totalPages,
        tableUpdate
    }: MonitorTableProps<ItemType>
): FunctionComponentReturnType {
    const [filters, setFilters] = React.useState<TableFiltersType<ItemType>>({});
    const [itemToDelete, setItemToDelete] = React.useState<ItemType | null>(null);

    function createColumns(): Column[] {
        const output: Column[] = columns.map((x: ColumnTitleAndDataIndex<ItemType>): Column => {
            if (x.filterable == false) {
                return createPlainColumn(x.title, x.dataIndex);
            }
            return createFilterableColumn(x.title, x.dataIndex, filters, setFilters);
        });

        output.push({ title: '', dataIndex: 'actions', key: 'actions' });

        return output;
    }

    function createDataSource(): MonitorTableDataSource<ItemType>[] {
        return items
            .filter(filterItems)
            .map(createMonitorTableDataSource);
    }

    function filterItems(item: ItemType): boolean {
        return filterItem(item, filters);
    }

    function createMonitorTableDataSource(item: ItemType): MonitorTableDataSource<ItemType> {

        return {
            key: getKey(item),
            actions: (
                <MonitorTableActionTray
                    onDelete={(): void => setItemToDelete(item)}
                />
            ),
            ...item
        };
    }

    return (
        <>
            { currentPage != undefined ? (
                    <div style={{display: 'flex', justifyContent: 'end', marginTop: '5px'}}>
                        <Button id={'copp-button-first-page-top'} className="pagination-button" onClick={()=>{tableUpdate("page", "first")}}>&lt;&lt;</Button>
                        <Button id={'copp-button-prev-page-top'} className="pagination-button" onClick={()=>{tableUpdate("page", "prev")}}>&lt;</Button>
                        <div style={{lineHeight: '30px', padding: "0px 5px"}}>{currentPage + " / " + (totalPages < 0 ? '?' : totalPages )}</div>
                        <Button id={'copp-button-next-page-top'} className="pagination-button" onClick={()=>{tableUpdate("page", "next")}}>&gt;</Button>
                        <Button id={'copp-button-last-page-top'} className="pagination-button" onClick={()=>{tableUpdate("page", "last")}} disabled={totalPages < 0}>&gt;&gt;</Button>            </div>)
                : null }

            <Table
                className="monitor"
                columns={createColumns()}
                dataSource={createDataSource()}
            />
            { currentPage != undefined ? (
                    <div style={{display: 'flex', justifyContent: 'end', marginTop: '5px'}}>
                        <Button id={'copp-button-first-page-bottom'} className="pagination-button" onClick={()=>{tableUpdate("page", "first")}}>&lt;&lt;</Button>
                        <Button id={'copp-button-prev-page-bottom'} className="pagination-button" onClick={()=>{tableUpdate("page", "prev")}}>&lt;</Button>
                        <div style={{lineHeight: '30px', padding: "0px 5px"}}>{currentPage + " / " + (totalPages < 0 ? '?' : totalPages )}</div>
                        <Button id={'copp-button-next-page-bottom'} className="pagination-button" onClick={()=>{tableUpdate("page", "next")}}>&gt;</Button>
                        <Button id={'copp-button-last-page-bottom'} className="pagination-button" onClick={()=>{tableUpdate("page", "last")}} disabled={totalPages < 0}>&gt;&gt;</Button>
                    </div>)
                : null }

            <ConfirmDeleteModal
                isOpen={Boolean(itemToDelete)}
                title={modalTitle}
                description={itemToDelete ? getModalDescription(itemToDelete) : ''}
                apiRouteToDelete={itemToDelete ? getApiRouteToDelete(itemToDelete) : null}
                onClose={(): void => setItemToDelete(null)}
                onAfterDelete={refreshData}
            />
        </>
    );
}

export default MonitorTable;
